import { useEffect, useState } from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import { ArrowLeft, ArrowRight, Check, MapPin, Phone, ShieldCheck } from "lucide-react";
import { SERVICES } from "@/data/site";
import { getCity } from "@/lib/api";
import { useSite } from "@/lib/site";
import Seo, { ORG_SCHEMA, SITE_URL } from "@/components/Seo";
import ContactForm from "@/components/ContactForm";
import CityLinks from "@/components/CityLinks";

const buildFaqs = (service, city) => [
  { q: `Do you provide ${service.title.toLowerCase()} in ${city.name}?`, a: `Yes. Digi Hub Solution serves businesses across ${city.name} and the wider ${city.region} region, both on-site and remotely.` },
  { q: `How much does ${service.title.toLowerCase()} cost in ${city.name}?`, a: `Every project is priced to its scope. Book a free consultation and we'll send a clear quote for your ${city.name} business within 48 hours.` },
  { q: `Can your team visit our office in ${city.name}?`, a: `Absolutely. We schedule site visits in ${city.name} for discovery, setup and training, and support you remotely in between.` },
  { q: `How quickly can we get started?`, a: `Most ${city.name} projects kick off within a week of the discovery call, with a firm timeline shared upfront.` },
];

const buildSchema = (service, city, faqs, url) => [
  {
    "@context": "https://schema.org",
    "@type": "Service",
    name: `${service.title} in ${city.name}`,
    description: city.intro,
    image: service.image,
    url,
    serviceType: service.title,
    areaServed: { "@type": "City", name: city.name, containedInPlace: { "@type": "State", name: city.region } },
    provider: ORG_SCHEMA,
  },
  {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: faqs.map((f) => ({ "@type": "Question", name: f.q, acceptedAnswer: { "@type": "Answer", text: f.a } })),
  },
];

export default function CityService() {
  const { slug, city: citySlug } = useParams();
  const service = SERVICES.find((s) => s.slug === slug);
  const [city, setCity] = useState(null);
  const [missing, setMissing] = useState(false);

  useEffect(() => {
    setCity(null); setMissing(false);
    getCity(citySlug).then(setCity).catch(() => setMissing(true));
  }, [citySlug]);

  if (!service) return <Navigate to="/services" replace />;
  if (missing) return <Navigate to={`/services/${slug}`} replace />;
  if (!city) return <div className="container-x py-32 text-center text-[color:var(--dh-slate)]" data-testid="city-loading">Loading…</div>;

  const faqs = buildFaqs(service, city);
  const url = `${SITE_URL}/services/${service.slug}/${city.slug}`;

  return (
    <div data-testid={`city-page-${service.slug}-${city.slug}`}>
      <Seo
        title={`${service.title} in ${city.name}, ${city.region}`}
        description={`${service.title} in ${city.name}: ${city.intro}`.slice(0, 158)}
        image={service.image}
        url={url}
        jsonLd={buildSchema(service, city, faqs, url)}
      />
      <CityHero service={service} city={city} />
      <Deliverables service={service} city={city} />
      <WhyUs city={city} />
      <LocalFaq faqs={faqs} city={city} />
      <section id="enquire" className="container-x pb-8 scroll-mt-24">
        <div className="max-w-3xl">
          <ContactForm
            service={service.title}
            city={city.name}
            heading={`Get a free ${service.title.toLowerCase()} quote in ${city.name}`}
            subheading="Tell us what you need. We usually reply within one business day."
          />
        </div>
      </section>
      <CityLinks service={service} exclude={city.slug} title={`${service.title} in nearby cities.`} />
      <OtherServices service={service} city={city} />
    </div>
  );
}

function CityHero({ service, city }) {
  const { site } = useSite();
  const phone = site.contact?.phone1 || "";
  return (
    <section className="hero-gradient text-white overflow-hidden">
      <div className="container-x py-16 md:py-20">
        <Link to={`/services/${service.slug}`} className="inline-flex items-center gap-2 text-white/70 hover:text-white text-sm" data-testid="back-to-service">
          <ArrowLeft className="w-4 h-4" /> {service.title}
        </Link>
        <div className="mt-6 grid md:grid-cols-2 gap-10 items-center">
          <div>
            <div className="chip-light reveal d1" data-testid="city-chip"><MapPin className="w-3.5 h-3.5" /> {city.name}, {city.region}</div>
            <h1 className="reveal d2 mt-4 text-4xl sm:text-5xl lg:text-6xl leading-[1.05]" data-testid="city-hero-title">
              {service.title} in <span className="text-[color:var(--dh-orange)]">{city.name}</span>
            </h1>
            <p className="reveal d3 mt-5 text-white/80 max-w-xl leading-relaxed" data-testid="city-intro">{city.intro}</p>
            <div className="mt-8 flex flex-wrap gap-3">
              <a href="#enquire" className="btn-primary" data-testid="city-hero-cta">Get a Free Quote <ArrowRight className="w-4 h-4" /></a>
              {phone && <a href={`tel:${phone.replace(/\s/g, "")}`} className="btn-outline" data-testid="city-hero-call"><Phone className="w-4 h-4" /> {phone}</a>}
            </div>
          </div>
          <div className="relative">
            <div className="absolute -top-6 -right-6 w-40 h-40 rounded-full bg-[color:var(--dh-orange)]/30 blur-2xl" />
            <img src={service.image} alt={`${service.title} in ${city.name}`} className="relative rounded-3xl border border-white/15 w-full h-[340px] object-cover" />
          </div>
        </div>
      </div>
    </section>
  );
}

function Deliverables({ service, city }) {
  return (
    <section className="container-x py-20">
      <div className="grid md:grid-cols-3 gap-10">
        <div>
          <div className="eyebrow">What You Get</div>
          <h2 className="mt-3 text-3xl md:text-4xl text-[color:var(--dh-navy)]">{service.title} built for {city.name} businesses.</h2>
          <p className="mt-4 text-[color:var(--dh-slate)] leading-relaxed">{service.long}</p>
        </div>
        <div className="md:col-span-2 grid sm:grid-cols-2 gap-4">
          {service.bullets.map((b) => (
            <div key={b} className="p-5 rounded-2xl border border-[color:var(--dh-line)] bg-white flex gap-3 hover:border-[color:var(--dh-orange)] transition-colors">
              <Check className="w-5 h-5 mt-0.5 text-[color:var(--dh-orange)] shrink-0" />
              <div className="font-display font-bold text-[color:var(--dh-navy)]">{b}</div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function WhyUs({ city }) {
  if (!city.why?.length) return null;
  return (
    <section className="bg-[color:var(--dh-cream)] py-20" data-testid="city-why">
      <div className="container-x">
        <div className="eyebrow">Why Digi Hub</div>
        <h2 className="mt-3 text-3xl md:text-4xl text-[color:var(--dh-navy)]">Why {city.name} chooses us.</h2>
        <div className="mt-10 grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {city.why.map((w, i) => (
            <div key={w} className="p-6 rounded-2xl border border-[color:var(--dh-line)] bg-white" data-testid={`city-why-${i}`}>
              <div className="w-10 h-10 rounded-xl bg-[color:var(--dh-orange)] text-white grid place-items-center"><ShieldCheck className="w-5 h-5" /></div>
              <p className="mt-4 font-display font-bold text-[color:var(--dh-navy)] leading-snug">{w}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function LocalFaq({ faqs, city }) {
  return (
    <section className="container-x py-20">
      <div className="grid md:grid-cols-[1fr_1.5fr] gap-10">
        <div>
          <div className="eyebrow">FAQs</div>
          <h2 className="mt-3 text-3xl md:text-4xl text-[color:var(--dh-navy)]">Questions from {city.name}.</h2>
        </div>
        <div className="space-y-3">
          {faqs.map((f, i) => (
            <details key={f.q} className="group p-5 rounded-2xl border border-[color:var(--dh-line)] bg-white" data-testid={`city-faq-${i}`}>
              <summary className="cursor-pointer font-display font-bold text-[color:var(--dh-navy)] flex items-center justify-between gap-4 list-none">
                {f.q} <span className="transition-transform group-open:rotate-45 text-[color:var(--dh-orange)] text-2xl leading-none">+</span>
              </summary>
              <p className="mt-3 text-[color:var(--dh-slate)]">{f.a}</p>
            </details>
          ))}
        </div>
      </div>
    </section>
  );
}

function OtherServices({ service, city }) {
  const others = SERVICES.filter((s) => s.slug !== service.slug);
  return (
    <section className="bg-[color:var(--dh-navy)] text-white py-20">
      <div className="container-x">
        <h2 className="text-3xl md:text-4xl">More services in {city.name}</h2>
        <div className="mt-8 grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {others.map((s) => (
            <Link key={s.slug} to={`/services/${s.slug}/${city.slug}`} className="p-5 rounded-2xl bg-white/5 border border-white/10 hover:bg-white/10 transition-colors flex items-center justify-between gap-3" data-testid={`city-other-${s.slug}`}>
              <span className="font-display">{s.title}</span>
              <ArrowRight className="w-4 h-4 text-[color:var(--dh-orange)] shrink-0" />
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
}
